/**
 * Insurance Connector Provider
 *
 * React context provider for the Insurance Connector XState machine
 */

"use client";

import { useActorRef, useSelector } from "@xstate/react";
import { usePathname } from "next/navigation";
import {
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  useBoundStore,
  useBoundStoreBase,
} from "@frontend/lib/stores/globalStore";
import { defaultConsumer } from "@frontend/lib/stores/slices/consumerSlice";
import { defaultUser } from "@frontend/lib/stores/slices/userSlice";
import { insuranceConnectorMachine } from "./machine/insurance-connector.machine";
import type { InsuranceConnectorInput } from "./machine/types";
import type { ActorRefFrom } from "xstate";

type MatchesFn = (stateValue: string | Record<string, unknown>) => boolean;

interface InsuranceConnectorProviderProps {
  children: ReactNode;
  input: InsuranceConnectorInput;
  autoOpen?: boolean;
}

type InsuranceConnectorActorRef = ActorRefFrom<
  typeof insuranceConnectorMachine
>;

const InsuranceConnectorContext =
  createContext<InsuranceConnectorActorRef | null>(null);
const NavButtonRefContext = createContext<RefObject<HTMLElement | null> | null>(
  null,
);

/**
 * Provider component for Insurance Connector machine
 */
export function InsuranceConnectorProvider({
  children,
  input,
  autoOpen = false,
}: InsuranceConnectorProviderProps) {
  const actorRef = useActorRef(insuranceConnectorMachine, {
    input,
  });
  const navButtonRef = useRef<HTMLElement | null>(null);
  const hasAutoOpened = useRef(false);
  // Tracks whether we've already performed a meaningful first-mount pass
  // — either hydrating the machine from persisted state or wiping stale
  // partial data left over from a previous session. Once that has
  // happened, any later store writes (e.g. the user disconnecting
  // insurance via the nav menu clears `insurances` and flips
  // `isInsuranceValidated`) must NOT be reinterpreted as "stale data" by
  // the cleanup branch below, otherwise we'd clobber the consumer/user
  // identity that the review page, cart pricing, and scheduling still
  // actively depend on.
  const hasCompletedInitialPass = useRef(false);

  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();
  const insurances = useBoundStore.use.insurances();
  const consumer = useBoundStore.use.consumer();
  const user = useBoundStore.use.user();
  const lastInsuranceCheckResults =
    useBoundStore.use.lastInsuranceCheckResults();
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();

  // Hydrate the machine from the global store as soon as the actor is
  // started.
  //
  // Semantics:
  //   - If the store holds a verified connection (`isInsuranceValidated`)
  //     plus the primary insurance identifiers and complete identity,
  //     hydrate the machine into `connected`.
  //   - Otherwise, if the store has any stale partial data hanging around
  //     (e.g. from a previous unverified/eligibility-fail flow), clear it
  //     so the user starts fresh.
  //   - If the store is empty, do nothing and let the effect re-run on
  //     future updates (e.g. if zustand's rehydration arrives on a later
  //     tick).
  //
  // Idempotency comes from the machine itself: `HYDRATE_FROM_STORE` is
  // only handled in `closed`, so once we've hydrated the actor the effect
  // turns into a no-op on subsequent fires. Similarly, once we clear
  // stale data the next run finds nothing to clear.
  //
  // We intentionally use `useEffect` (not `useLayoutEffect`) because
  // `@xstate/react`'s `useActorRef` starts the actor inside a regular
  // `useEffect`, and we need the actor to be started before `.send()`
  // can reach it. Effects fire in registration order, so the start
  // effect runs before ours.
  // biome-ignore lint/correctness/useExhaustiveDependencies: hook values are intentionally kept in the dep array (but not read in the body) as a safety net so the effect re-fires if the store updates later. The body reads fresh state via `getState()` instead — see comment below.
  useEffect(() => {
    const snapshot = actorRef.getSnapshot();
    if (!snapshot.matches("closed")) {
      // The machine has moved beyond its initial `closed` state — either
      // we hydrated from the store or the user walked through the
      // in-session connection flow. Either way, anything currently
      // sitting in the consumer/user/insurance rows is authoritative and
      // must not be treated as "stale" by the cleanup branch below if
      // the user later disconnects (which returns the machine to
      // `closed` with identity still legitimately populated).
      hasCompletedInitialPass.current = true;
      return;
    }

    // Read directly from the store via `getState()` instead of relying on
    // the `useBoundStore.use.*()` hook closures above. Zustand's `persist`
    // middleware finishes rehydrating asynchronously (even with sync
    // sessionStorage), so the hook values captured at render time are
    // still defaults on the first effect tick — but `getState()` already
    // reflects the rehydrated values. The hook subscriptions are still
    // wired into the effect's dependency array so later store updates
    // continue to re-trigger this effect as a safety net.
    const {
      insurances: storeInsurances,
      consumer: storeConsumer,
      user: storeUser,
      isInsuranceValidated: storeIsInsuranceValidated,
      lastInsuranceCheckResults: storeLastInsuranceCheckResults,
      eligibilityCheckResult: storeEligibilityCheckResult,
      minimumAge: storeMinimumAge,
      isPendingPricesMode: storeIsPendingPricesMode,
    } = useBoundStoreBase.getState();

    // Rehydrate eligibility context so page reloads keep the machine's
    // guards in agreement with the persisted `eligibilityCheckResult`.
    // We stay in `closed` — the next `OPEN` will route correctly.
    if (
      storeEligibilityCheckResult?.result === "failed" &&
      (storeEligibilityCheckResult.code === "under_aged" ||
        storeEligibilityCheckResult.code === "state_not_available" ||
        storeEligibilityCheckResult.code === "minor_consent_referral") &&
      !snapshot.context.eligibilityFailureCode
    ) {
      actorRef.send({
        type: "HYDRATE_ELIGIBILITY_FROM_STORE",
        data: {
          eligibilityFailureCode: storeEligibilityCheckResult.code,
          consumerState: storeEligibilityCheckResult.state,
          minimumAge: storeMinimumAge || undefined,
        },
      });
    }

    if (
      storeEligibilityCheckResult?.result === "passed" &&
      !snapshot.context.passedEligibility
    ) {
      actorRef.send({
        type: "HYDRATE_ELIGIBILITY_PASSED_FROM_STORE",
        data: {
          birthday: storeEligibilityCheckResult.birthday,
          consumerState: storeEligibilityCheckResult.state,
        },
      });
    }

    const primary = storeInsurances?.[0];

    const hasFullIdentity =
      !!storeConsumer?.fname &&
      !!storeConsumer?.lname &&
      !!storeConsumer?.dob &&
      !!storeUser?.phone;

    const hasInsurance = !!primary?.payer && !!primary?.memberId;

    // `isInsuranceValidated` is only set to `true` after a successful
    // active-outcome verification (see `syncAllToStore`), so we treat it
    // as the authoritative "fully connected" signal — no need to
    // second-guess the stored `status` string.
    const canHydrate =
      storeIsInsuranceValidated && hasInsurance && hasFullIdentity;

    if (canHydrate && primary) {
      actorRef.send({
        type: "HYDRATE_FROM_STORE",
        data: {
          firstName: storeConsumer?.fname || storeUser?.fname || "",
          lastName: storeConsumer?.lname || storeUser?.lname || "",
          birthday: storeConsumer?.dob || "",
          phoneNumber: storeUser?.phone || "",
          consumerId: storeConsumer?.id || undefined,
          userId: storeUser?.id || undefined,
          primaryInsurance: {
            payer: primary.payer,
            memberId: primary.memberId,
            insuredState: primary.state || "",
            planName: primary.planName ?? undefined,
          },
          lastVerificationResults: storeLastInsuranceCheckResults ?? undefined,
        },
      });
      hasCompletedInitialPass.current = true;
      return;
    }

    // Pending-prices mode means the user intentionally proceeded with
    // unverified insurance, so the insurance/consumer/user rows in the
    // store are NOT stale — they are the authoritative record we need to
    // keep around for review / checkout / appointment creation. Preserve
    // them as-is; the machine stays in `closed` and the UI treats the
    // user as "effectively connected" via `isPendingPricesMode`.
    if (storeIsPendingPricesMode && hasInsurance && hasFullIdentity) {
      hasCompletedInitialPass.current = true;
      return;
    }

    // The stale-data cleanup below is only meant to handle leftover
    // partial state from a *previous* session (e.g. a page reload after
    // a failed eligibility flow). Once we've done our first meaningful
    // pass, any further store changes reflect in-session user actions
    // — most importantly, disconnecting insurance from the nav menu
    // clears `insurances` and flips `isInsuranceValidated`. Wiping the
    // consumer/user identity at that point would break the review
    // page's basic-info section, cart pricing, and scheduling, all of
    // which legitimately still need name/dob/phone/email. So gate the
    // cleanup behind the first-pass sentinel.
    if (hasCompletedInitialPass.current) return;

    const hasAnyStaleInsurance =
      !!storeInsurances?.length || !!storeLastInsuranceCheckResults;
    const hasAnyStaleIdentity =
      !!storeConsumer?.fname ||
      !!storeConsumer?.lname ||
      !!storeConsumer?.dob ||
      !!storeUser?.fname ||
      !!storeUser?.lname ||
      !!storeUser?.phone ||
      !!storeUser?.email;

    if (hasAnyStaleInsurance || hasAnyStaleIdentity) {
      const {
        setInsurances,
        setLastInsuranceCheckResults,
        setConsumer,
        setUser,
      } = useBoundStoreBase.getState();
      if (hasAnyStaleInsurance) {
        setInsurances([]);
        setLastInsuranceCheckResults(null);
      }
      if (hasAnyStaleIdentity) {
        setConsumer(defaultConsumer);
        setUser(defaultUser);
      }
    }

    // Mark the first pass complete unconditionally. Previously this flag was
    // only flipped when we actually cleaned something up, which left
    // `hasCompletedInitialPass` false on the very common "empty store on
    // mount" path (e.g. shop review page for a fresh session). When later
    // in-session writes populated identity — most notably `MFAComponent`
    // calling `setUser`/`setConsumer` after a successful OTP — this effect
    // re-fired and misclassified that fresh data as stale, wiping it and
    // bouncing the user back to the "Enter your phone or email" screen.
    //
    // Semantics: once we've inspected the store exactly once, any further
    // identity/insurance mutations come from legitimate in-session user
    // actions and must not be cleared by this effect.
    hasCompletedInitialPass.current = true;
  }, [
    actorRef,
    isInsuranceValidated,
    insurances,
    consumer,
    user,
    lastInsuranceCheckResults,
    eligibilityCheckResult,
    isPendingPricesMode,
  ]);

  // Auto-open the modal on first mount for orgs that want immediate
  // insurance connection (e.g. orgs with insuranceStepper in their
  // render config).
  //
  // The hydration effect above is declared first, so it fires first
  // in the same commit. If the store already had a valid connection,
  // hydration will have synchronously transitioned the machine out of
  // `closed` before we run — the snapshot check below catches that.
  //
  // We also blacklist the review/complete routes: those pages have their
  // own purchase-finalization UI and popping the insurance modal over
  // them mid-checkout is disruptive (it's the same rationale behind
  // hiding the nav insurance status button on those routes).
  const pathname = usePathname();
  useEffect(() => {
    if (!autoOpen || hasAutoOpened.current) return;

    const isAutoOpenBlacklistedRoute =
      !!pathname &&
      (pathname.includes("/review") ||
        pathname.includes("/complete") ||
        pathname.includes("/schedule"));
    if (isAutoOpenBlacklistedRoute) return;

    hasAutoOpened.current = true;

    const snapshot = actorRef.getSnapshot();
    if (!snapshot.matches("closed")) return;

    actorRef.send({ type: "OPEN" });
  }, [autoOpen, actorRef, pathname]);

  return (
    <InsuranceConnectorContext.Provider value={actorRef}>
      <NavButtonRefContext.Provider value={navButtonRef}>
        {children}
      </NavButtonRefContext.Provider>
    </InsuranceConnectorContext.Provider>
  );
}

/**
 * Hook to access the nav button ref (for anchoring the minimized panel)
 */
export function useNavButtonRef() {
  return useContext(NavButtonRefContext);
}

function useInsuranceConnectorActorRef() {
  const actorRef = useContext(InsuranceConnectorContext);
  if (!actorRef) {
    throw new Error(
      "useInsuranceConnectorActorRef must be used within InsuranceConnectorProvider",
    );
  }
  return actorRef;
}

/**
 * Hook to access the full Insurance Connector machine (state and send)
 */
export function useInsuranceConnector() {
  const actorRef = useInsuranceConnectorActorRef();
  const snapshot = useSelector(actorRef, (state) => state);

  return useMemo(
    () => ({
      state: {
        context: snapshot.context,
        value: snapshot.value,
        matches: (stateValue: string | Record<string, unknown>) =>
          (snapshot.matches as MatchesFn)(stateValue),
        can: snapshot.can,
      },
      send: actorRef.send,
      actorRef,
    }),
    [snapshot, actorRef],
  );
}

/**
 * Hook to access Insurance Connector machine state
 */
export function useInsuranceConnectorState() {
  const actorRef = useInsuranceConnectorActorRef();
  const snapshot = useSelector(actorRef, (state) => state);

  return useMemo(
    () => ({
      context: snapshot.context,
      value: snapshot.value,
      matches: (stateValue: string | Record<string, unknown>) =>
        (snapshot.matches as MatchesFn)(stateValue),
      can: snapshot.can,
    }),
    [snapshot],
  );
}

/**
 * Hook to access only the send function
 */
export function useInsuranceConnectorSend() {
  const actorRef = useInsuranceConnectorActorRef();
  return actorRef.send;
}

/**
 * Hook to check if machine is in a specific state
 */
export function useInsuranceConnectorMatches(stateValue: string) {
  const actorRef = useInsuranceConnectorActorRef();
  const snapshot = useSelector(actorRef, (state) => state);
  return (snapshot.matches as MatchesFn)(stateValue);
}

/**
 * Hook to access machine context
 */
export function useInsuranceConnectorContext() {
  const actorRef = useInsuranceConnectorActorRef();
  const context = useSelector(actorRef, (state) => state.context);
  return context;
}

/**
 * Hook that wraps a price toggle handler with an insurance connection gate.
 * - If insurance is not connected and the modal is closed, opens the modal
 *   instead of toggling.
 * - If the modal is already open (full or minimized), lets the toggle work
 *   normally so the user isn't blocked.
 * - If insurance is connected but the verification outcome was NOT "active"
 *   (and we're not in pending-prices mode), the user is locked to self-pay.
 *   Flipping the toggle to INSURED in that state is a no-op because there's
 *   no INSURED price data backing it — without this gate the prices would
 *   render as $0 / $NaN.
 *
 * Returns a `createGatedHandler` factory so callers with multiple toggle
 * handlers (e.g. default + initial-required) only need one hook call.
 */
export function useInsuranceGatedToggle() {
  const insuranceState = useInsuranceConnectorState();
  const sendInsurance = useInsuranceConnectorSend();
  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const isInsuranceConnected =
    insuranceState.matches("connected") || insuranceState.matches("minimized");
  // Only an `active`-outcome verification (which sets
  // `isInsuranceValidated`) or an explicit pending-prices override
  // (proceed-with-unverified) entitles the user to see INSURED prices.
  // Inactive / out-of-network / unverified-no-flag connections keep the
  // insurance card on file but must remain locked to self-pay.
  const canUseInsuredPricing = isInsuranceValidated || isPendingPricesMode;

  const createGatedHandler =
    (onToggle: (checked: boolean) => void) => (checked: boolean) => {
      if (checked && !isInsuranceConnected) {
        if (insuranceState.context.uiMode === "closed") {
          sendInsurance({ type: "OPEN" });
          return;
        }
      }
      if (checked && !canUseInsuredPricing) {
        // No active insurance, no pending-prices override: silently drop
        // the attempt to flip to INSURED prices. The visual switch is
        // expected to be hidden / disabled in this state by the caller
        // (see HrtSelector's `canShowInsuredToggle`).
        return;
      }
      onToggle(checked);
    };

  return {
    createGatedHandler,
    isInsuranceConnected,
    canUseInsuredPricing,
    insuranceState,
    sendInsurance,
  };
}
